// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';

// import global components
import Spinner from '../../../global/components/helpers/Spinner.jsx';
import { usePageTitle } from '../../../global/utils/customHooks.js';

/**
 * Wraps all non-admin User components in a default view wrapper.
 * @param title - the title of the page (shows up on the browser tab)
 * @param className - the className to apply to the wrapper
 */
const UserLayout = ({ title, children, className = 'bg-neutral-pri w-full min-h-screen pt-10 md:pt-20' }) => {

  usePageTitle(title ? `${title} | Simulations Plus` : "User | Simulations Plus");

  return (
    <div className={className}>
      {children}
    </div>
  )
}

UserLayout.Skeleton = (props) => {
  return (
    <UserLayout {...props}>
      <Spinner />
    </UserLayout>
  )
}

UserLayout.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node)
    , PropTypes.node
  ]).isRequired
  , className: PropTypes.string
  , title: PropTypes.string
}
/**
 * The centered column shared by the auth views: white wordmark over a card, with the
 * legal links below it. Mirrors the chrome in UserLogin.jsx so the auth surfaces match.
 *
 * Deliberately differs from login's card in two ways: gap-4 rather than gap-2 (login's
 * child owns an internal gap-6 stack, ours are loose elements), and no items-center
 * (which would shrink-wrap a loose <form> or <p> to its content width).
 *
 * @param title - optional card heading. Omit it when the caller renders its own
 *   per-step heading, or for spinner-only states.
 * @param showLegal - set false to drop the Terms/Privacy links below the card.
 */
export const Container = ({ children, title, showLegal = true }) => {
  return (
    <div className='flex flex-col flex-nowrap items-center justify-start w-full max-w-full md:w-[520px] md:max-w-[520px] gap-6'>
      <img
        className="w-[232px] max-w-[80%] h-auto block flex-shrink-0"
        src="/img/SP_wordmark-white.png"
        alt="Simulations Plus"
      />
      <div className='bg-neutral-pri border border-neutral-pri rounded-lg flex flex-col flex-nowrap w-full gap-4 p-10'>
        {title ? <h1 className='text-2xl font-semibold text-neutral-pri m-0'>{title}</h1> : null}
        {children}
      </div>
      {showLegal && (
        // fixed white, not a theme token: the field behind it does not invert
        <div className='flex-grow-0 flex-shrink-0 flex flex-wrap gap-2 justify-center items-center text-tertiary-100'>
          <a href="https://www.simulations-plus.com/terms-of-use/" target="_blank" rel="noreferrer" className="text-xs no-underline my-auto !text-tertiary-100">Terms of Use</a>
          <span aria-hidden="true" className="opacity-60 text-xs">&middot;</span>
          <a href="https://www.simulations-plus.com/privacy-notice/" target="_blank" rel="noreferrer" className="text-xs no-underline my-auto !text-tertiary-100">Privacy Policy</a>
        </div>
      )}
    </div>
  )
}

Container.propTypes = {
  children: PropTypes.node
  , title: PropTypes.string
  , showLegal: PropTypes.bool
}

export default UserLayout;

